import { useState } from 'react';
import api from '@/lib/api';
import { Download, Loader2 } from 'lucide-react';

const BUTTON_CLASS = 'inline-flex items-center gap-1.5 border-2 border-[#e3e3e3] dark:border-white/10 '
  + 'text-[#30302F] dark:text-white hover:bg-[#f9f5fa] dark:hover:bg-white/5 rounded-full font-bold text-sm '
  + 'px-5 h-10 transition-colors disabled:opacity-50';

export function csvFilename(now = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  return `intake-requests-${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}.csv`;
}

// The filename from Content-Disposition (the server's date), else our own.
// Only a plain name is taken: no path, no quotes.
export function filenameFrom(disposition) {
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition || '');
  const name = m ? decodeURIComponent(m[1]).trim() : '';
  return /^[\w.\- ]+\.csv$/i.test(name) ? name : csvFilename();
}

function blobText(data) {
  if (typeof data?.text === 'function') return data.text();
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(reader.error);
      reader.readAsText(data);
    });
  }
  return Promise.resolve(String(data || ''));
}

async function errorText(err) {
  if (err?.response?.status === 403) return 'You do not have access to export these requests.';
  const data = err?.response?.data;
  // The response is a Blob because the request asked for one.
  try {
    const body = typeof data === 'object' && !(data instanceof Blob) ? data : JSON.parse(await blobText(data));
    if (typeof body?.detail === 'string') return body.detail;
  } catch {
    // fall through to the generic message
  }
  return 'The download did not work. Try again.';
}

// Downloads the register's current tab and filters as CSV, every page of it.
// `endpoint` is the tab's list route; the export route sits under it.
export default function DownloadCsvButton({ endpoint, params }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const download = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await api.get(`${endpoint}/export`, { params, responseType: 'blob' });
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filenameFrom(res.headers?.['content-disposition']);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(await errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col items-end gap-1">
      <button type="button" onClick={download} disabled={busy} className={BUTTON_CLASS} data-testid="intake-download-csv">
        {busy ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Download size={14} aria-hidden="true" />}
        Download CSV
      </button>
      {error && (
        <p className="text-xs text-red-700 dark:text-red-300 max-w-xs text-right" role="alert" data-testid="intake-download-error">
          {error}
        </p>
      )}
    </div>
  );
}
